/**
 * gagoz-card.js
 *
 * Custom element <gagoz-card> — handles:
 *   1. CSS-transform image slideshow (prev / next buttons + auto-advance)
 *   2. Viewport-entry auto-advance (IntersectionObserver, fires once per card)
 *   3. Fruit overlay animation:
 *        – Desktop: triggered by pointerenter, hidden after 3 s or on pointerleave
 *        – Mobile:  triggered after the viewport-entry slide transition ends
 */

class GagozCard extends HTMLElement {
  constructor() {
    super();

    /** @type {number} Current slide index (0 = last original image) */
    this.currentIndex = 0;
    /** @type {number} Total number of slides in the track */
    this.totalSlides = 0;
    /** @type {boolean} Prevents overlapping slide transitions */
    this.isAnimating = false;
    /** @type {boolean} True after the viewport-entry auto-advance has run */
    this.hasAdvanced = false;
    /** @type {ReturnType<typeof setTimeout>|null} Fruit hide timer */
    this.fruitTimer = null;
    /** @type {ReturnType<typeof setTimeout>|null} Viewport-entry advance delay timer */
    this.advanceTimer = null;
    /** @type {boolean} Whether the current viewport is mobile-sized */
    this.isMobile = window.matchMedia("(max-width: 749px)").matches;
    /** @type {boolean} Whether this card has more than one slide */
    this.hasSlideshow = false;
    /** @type {IntersectionObserver | null} */
    this._observer = null;
    /** @type {HTMLButtonElement[]} */
    this.variantButtons = [];

    // Bind handlers so they can be removed cleanly
    this._onEnter = this._onEnter.bind(this);
    this._onLeave = this._onLeave.bind(this);
    this._onVariantClick = this._onVariantClick.bind(this);
    this._onFormSubmit = this._onFormSubmit.bind(this);
  }

  connectedCallback() {
    this.slidesEl = /** @type {HTMLElement | null} */ (
      this.querySelector(".gagoz-card__slides")
    );
    this.fruitEl = /** @type {HTMLElement | null} */ (
      this.querySelector(".gagoz-card__fruit")
    );
    this.prevBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector(".gagoz-card__btn--prev")
    );
    this.nextBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector(".gagoz-card__btn--next")
    );
    this.variantButtons = Array.from(
      this.querySelectorAll(".gagoz-card__variant-btn"),
    );
    this.variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    this.atcBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector(".gagoz-card__atc-btn")
    );
    this.formEl = /** @type {HTMLFormElement | null} */ (
      this.querySelector(".gagoz-card__form")
    );
    this.titleLink = /** @type {HTMLAnchorElement | null} */ (
      this.querySelector(".gagoz-card__title-link")
    );
    this.priceRegularWrap = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-price-regular-wrap]")
    );
    this.priceRegular = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-price-regular]")
    );
    this.priceSaleWrap = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-price-sale-wrap]")
    );
    this.priceCompare = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-price-compare]")
    );
    this.priceSale = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-price-sale]")
    );

    this.totalSlides = parseInt(this.dataset.slideCount || "0", 10) || 0;
    this.hasSlideshow = this.totalSlides > 1;

    if (this.variantButtons.length > 0) {
      this.variantButtons.forEach((button) => {
        button.addEventListener("click", this._onVariantClick);
      });

      const initiallySelected =
        this.querySelector(".gagoz-card__variant-btn.is-selected") ||
        this.variantButtons[0];
      if (initiallySelected instanceof HTMLButtonElement) {
        this._selectVariant(initiallySelected);
      }
    }

    if (this.formEl) {
      this.formEl.addEventListener("submit", this._onFormSubmit);
    }

    // Slideshow features are skipped for single-image cards
    if (!this.hasSlideshow) return;

    // Initialise transform without animation
    this._setTransform(0, false);

    // Prev / Next button handlers
    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this._goTo(
          (this.currentIndex - 1 + this.totalSlides) % this.totalSlides,
        );
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this._goTo((this.currentIndex + 1) % this.totalSlides);
      });
    }

    // Desktop fruit animation (pointer events only on non-touch screens)
    if (!this.isMobile) {
      this.addEventListener("pointerenter", this._onEnter);
      this.addEventListener("pointerleave", this._onLeave);
    }

    // IntersectionObserver for auto-advance + mobile fruit trigger
    this._observer = new IntersectionObserver(
      (entries) => this._onIntersect(entries),
      { threshold: 0.4 },
    );
    this._observer.observe(this);
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    if (this.fruitTimer) clearTimeout(this.fruitTimer);
    if (this.advanceTimer) clearTimeout(this.advanceTimer);
    this.removeEventListener("pointerenter", this._onEnter);
    this.removeEventListener("pointerleave", this._onLeave);
    if (this.variantButtons) {
      this.variantButtons.forEach((button) => {
        button.removeEventListener("click", this._onVariantClick);
      });
    }
    if (this.formEl) {
      this.formEl.removeEventListener("submit", this._onFormSubmit);
    }
  }

  // ─── Variant picker ─────────────────────────────────────────────────────

  /** @param {MouseEvent} event */
  _onVariantClick(event) {
    event.preventDefault();
    this._selectVariant(/** @type {HTMLButtonElement} */ (event.currentTarget));
  }

  /** @param {HTMLButtonElement} button */
  _selectVariant(button) {
    if (!button) return;

    this.variantButtons.forEach((variantButton) => {
      const isActive = variantButton === button;
      variantButton.classList.toggle("is-selected", isActive);
      variantButton.setAttribute("aria-pressed", isActive ? "true" : "false");
    });

    if (this.variantIdInput && button.dataset.variantId) {
      this.variantIdInput.value = button.dataset.variantId;
    }

    if (this.titleLink && button.dataset.variantUrl) {
      this.titleLink.setAttribute("href", button.dataset.variantUrl);
    }

    this._updatePrice(button);
    this._updateAvailability(button);
  }

  /** @param {HTMLButtonElement} button */
  _updatePrice(button) {
    if (!this.priceRegularWrap || !this.priceSaleWrap) return;

    const price = button.dataset.variantPrice || "";
    const comparePrice = button.dataset.variantComparePrice || "";
    const hasComparePrice = comparePrice.length > 0;

    if (hasComparePrice) {
      this.priceRegularWrap.classList.add("price__hidden");
      this.priceSaleWrap.classList.remove("price__hidden");
      if (this.priceCompare) this.priceCompare.textContent = comparePrice;
      if (this.priceSale) this.priceSale.textContent = price;
      if (this.priceRegular) this.priceRegular.textContent = price;
      return;
    }

    this.priceSaleWrap.classList.add("price__hidden");
    this.priceRegularWrap.classList.remove("price__hidden");
    if (this.priceRegular) this.priceRegular.textContent = price;
  }

  /** @param {HTMLButtonElement} button */
  _updateAvailability(button) {
    if (!this.atcBtn) return;

    const available = button.dataset.variantAvailable === "true";
    this.atcBtn.disabled = !available;

    const buttonLabel = available
      ? this.dataset.atcLabel
      : this.dataset.soldOutLabel;
    if (buttonLabel) {
      this.atcBtn.textContent = buttonLabel;
    }
  }

  // ─── Add to cart (AJAX) ─────────────────────────────────────────────────

  /** @param {SubmitEvent} event */
  _onFormSubmit(event) {
    event.preventDefault();
    this._submitToCart();
  }

  async _submitToCart() {
    if (!this.formEl || !this.atcBtn || this.atcBtn.disabled) return;

    const formData = new FormData(this.formEl);
    const quantity = Number(formData.get("quantity")) || 1;
    const variantId = String(formData.get("id") || "");

    const cartItemComponents = document.querySelectorAll(
      "cart-items-component",
    );
    /** @type {string[]} */
    const sectionIds = [];
    cartItemComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    if (sectionIds.length > 0) {
      formData.set("sections", Array.from(new Set(sectionIds)).join(","));
    }

    this.atcBtn.disabled = true;

    try {
      const themeRoutes =
        /** @type {{ routes?: { cart_add_url?: string } } | undefined} */ (
          /** @type {any} */ (globalThis).Theme
        );
      const cartAddUrl = themeRoutes?.routes?.cart_add_url || "/cart/add.js";
      const response = await fetch(cartAddUrl, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.message || "Add to cart failed");
      }

      let cart = undefined;
      try {
        const cartResponse = await fetch("/cart.js");
        if (cartResponse.ok) {
          cart = await cartResponse.json();
        }
      } catch (_) {
        // cart payload is optional for drawer open behavior
      }

      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: this.id || String(this.dataset.productId || "gagoz-card"),
            data: {
              source: "product-form-component",
              itemCount: quantity,
              productId: this.dataset.productId,
              variantId,
              sections: result.sections,
            },
          },
        }),
      );
      window.location.href = "/cart";
    } catch (error) {
      console.error(error);
    } finally {
      const selectedButton = this.querySelector(
        ".gagoz-card__variant-btn.is-selected",
      );
      if (selectedButton instanceof HTMLButtonElement) {
        this._updateAvailability(selectedButton);
      } else if (this.atcBtn) {
        this.atcBtn.disabled = false;
      }
    }
  }

  // ─── IntersectionObserver ────────────────────────────────────────────────

  /** @param {IntersectionObserverEntry[]} entries */
  _onIntersect(entries) {
    const entry = entries[0];
    if (!entry) return;
    if (!entry.isIntersecting || this.hasAdvanced) return;

    this.hasAdvanced = true;
    if (this._observer) this._observer.disconnect();

    // Wait 2 s after becoming visible, then advance one slide
    const nextIndex = (this.currentIndex + 1) % this.totalSlides;
    this.advanceTimer = setTimeout(() => {
      this._goTo(nextIndex, () => {
        if (this.isMobile && this.fruitEl) {
          this._triggerFruit();
        }
      });
    }, 2000);
  }

  // ─── Desktop hover ───────────────────────────────────────────────────────

  _onEnter() {
    // Wait until the card has already done its initial auto-advance
    if (!this.fruitEl || !this.hasAdvanced) return;
    this._triggerFruit();
  }

  _onLeave() {
    if (!this.fruitEl) return;
    if (this.fruitTimer) {
      clearTimeout(this.fruitTimer);
      this.fruitTimer = null;
    }
    this._hideFruit();
  }

  // ─── Fruit animation ─────────────────────────────────────────────────────

  _triggerFruit() {
    if (!this.fruitEl) return;

    // Cancel any running hide timer
    if (this.fruitTimer) {
      clearTimeout(this.fruitTimer);
      this.fruitTimer = null;
    }

    // Abort any in-progress hide transition first
    this.fruitEl.classList.remove("is-hiding");

    // Force a reflow so removing is-hiding and adding is-visible
    // are treated as separate frames by the browser
    void this.fruitEl.offsetWidth;

    this.fruitEl.classList.add("is-visible");

    // Auto-hide after 3 s
    this.fruitTimer = setTimeout(() => {
      this.fruitTimer = null;
      this._hideFruit();
    }, 3000);
  }

  _hideFruit() {
    if (!this.fruitEl) return;
    if (!this.fruitEl.classList.contains("is-visible")) return;

    const fruitEl = this.fruitEl;

    fruitEl.classList.remove("is-visible");
    fruitEl.classList.add("is-hiding");

    const onTransitionEnd = () => {
      fruitEl.classList.remove("is-hiding");
      fruitEl.removeEventListener("transitionend", onTransitionEnd);
    };
    fruitEl.addEventListener("transitionend", onTransitionEnd);
  }

  // ─── Slide navigation ────────────────────────────────────────────────────

  /**
   * Transition to slide at `index`.
   * @param {number}        index      Target slide index
   * @param {Function|null} [onDone]   Callback fired after the transition ends
   */
  _goTo(index, onDone = null) {
    if (!this.slidesEl) return;
    if (this.isAnimating) return;
    this.isAnimating = true;
    this.currentIndex = index;

    this._setTransform(index, true);

    const slidesEl = this.slidesEl;

    const onTransitionEnd = () => {
      this.isAnimating = false;
      slidesEl.removeEventListener("transitionend", onTransitionEnd);
      if (onDone) onDone();
    };
    slidesEl.addEventListener("transitionend", onTransitionEnd);
  }

  /**
   * Apply translateX to the slides track.
   *
   * Track width = 100% * N  → each slide = 1/N of track = 100% of container.
   * To show slide i: translateX( -(i / N) * 100% of track ) = -i*(100/N)%
   *
   * @param {number}  index   Slide index to show
   * @param {boolean} animate Whether to use the CSS transition
   */
  _setTransform(index, animate) {
    if (!this.slidesEl) return;

    const slidesEl = this.slidesEl;

    if (!animate) {
      slidesEl.style.transition = "none";
    }

    const offset = (100 / this.totalSlides) * index;
    slidesEl.style.transform = `translateX(-${offset}%)`;

    if (!animate) {
      // Force reflow so the browser registers the instant position change
      // before the transition is restored
      void slidesEl.offsetWidth;
      slidesEl.style.transition = "";
    }
  }
}

customElements.define("gagoz-card", GagozCard);
